import React, { useEffect, useRef, useState } from "react"

import { toast } from "sonner"

import Icon from "@/components/Icon"
import { cn } from "@/lib/utils"

const COPIED_DURATION_MS = 500

type CopyButtonProps = {
  value: string
  /** What is being copied, for the accessible name - "Copy user ID". */
  label: string
  className?: string
}

export const CopyButton: React.FC<CopyButtonProps> = ({ value, label, className }) => {
  const [copied, setCopied] = useState(false)
  const resetTimer = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => () => clearTimeout(resetTimer.current), [])

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value)
    } catch {
      toast.error("Could not copy to clipboard")
      return
    }

    clearTimeout(resetTimer.current)
    setCopied(true)
    resetTimer.current = setTimeout(() => setCopied(false), COPIED_DURATION_MS)
  }

  return (
    <button
      type="button"
      onClick={copy}
      aria-label={copied ? "Copied" : `Copy ${label}`}
      title={copied ? "Copied" : `Copy ${label}`}
      className={cn(
        "inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-palette-jungleGreen500 transition-colors hover:bg-palette-jungleGreen100 hover:text-palette-jungleGreen900",
        className,
      )}
    >
      <Icon
        name={copied ? "check" : "copy"}
        className={cn("h-3.5 w-3.5", copied ? "text-indicators-green" : "text-current")}
      />
    </button>
  )
}
